/**
 * Simplified UTM tracking utility
 * Captures UTM parameters and stores them for API calls
 */

// List of UTM parameters to track
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id'
];

// Ad click IDs. Google Ads auto-tagging adds only gclid (or gbraid/wbraid on
// iOS), with no utm_*, so without these a paid click looks like direct traffic
// on the order (May-Jun 2026's Google Ads orders all did).
const CLICK_ID_SOURCES = {
  gclid: 'google',
  gbraid: 'google',
  wbraid: 'google',
  fbclid: 'meta',
  msclkid: 'bing',
};

// Storage key for UTM parameters
const UTM_STORAGE_KEY = 'fleek_utm_params';

/**
 * Captures UTM parameters from URL and stores them in localStorage
 * Always overwrites existing UTM data with latest source
 */
const captureUTMParams = () => {
  // Parse URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  let hasUtmParams = false;

  // Extract UTM parameters from URL
  UTM_PARAMS.forEach(param => {
    if (urlParams.has(param)) {
      utmParams[param] = urlParams.get(param);
      hasUtmParams = true;
    }
  });

  // The first click ID present wins; it names the source when utm_* doesn't.
  const clickParam = Object.keys(CLICK_ID_SOURCES).find((param) => urlParams.get(param));
  if (clickParam) {
    utmParams.click_id_type = clickParam;
    utmParams.click_id = urlParams.get(clickParam).slice(0, 200);
    if (!utmParams.utm_source) utmParams.utm_source = CLICK_ID_SOURCES[clickParam];
    if (!utmParams.utm_medium) utmParams.utm_medium = 'cpc';
    hasUtmParams = true;
  }

  // If UTM parameters exist in the URL, overwrite existing data
  if (hasUtmParams) {
    // Add timestamp and page info for attribution
    utmParams.timestamp = Date.now();
    utmParams.landing_page = window.location.pathname;
    utmParams.referrer = document.referrer;
    
    // Always overwrite - latest UTM source wins
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  }
};

/**
 * Retrieves stored UTM parameters
 * @returns {Object|null} UTM parameters object or null if not available
 */
const getUTMParams = () => {
  const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
  if (!storedParams) return null;
  
  try {
    return JSON.parse(storedParams);
  } catch (e) {
    console.error('Error parsing stored UTM parameters', e);
    return null;
  }
};

/**
 * Adds UTM parameters to API payload as 'metadata' field for order creation
 * @param {Object} payload - The original API request payload
 * @returns {Object} Updated payload with UTM parameters in metadata field
 */
const addUTMNotesToPayload = (payload = {}) => {
  const utmParams = getUTMParams();
  if (!utmParams) return payload;

  // Create UTM metadata object for API
  const utmMetadata = {
    attribution: {
      source: utmParams.utm_source || null,
      medium: utmParams.utm_medium || null,
      campaign: utmParams.utm_campaign || null,
      term: utmParams.utm_term || null,
      content: utmParams.utm_content || null,
      id: utmParams.utm_id || null,
      click_id_type: utmParams.click_id_type || null,
      click_id: utmParams.click_id || null,
      landing_page: utmParams.landing_page || null,
      referrer: utmParams.referrer || null,
      captured_at: utmParams.timestamp || null
    }
  };

  return {
    ...payload,
    metadata: utmMetadata
  };
};

/**
 * Gets UTM data for analytics (Facebook CAPI, etc.)
 * @returns {Object} UTM parameters for analytics platforms
 */
const getUTMForAnalytics = () => {
  const utmParams = getUTMParams();
  if (!utmParams) return {};

  // Return clean UTM object for analytics
  return {
    utm_source: utmParams.utm_source,
    utm_medium: utmParams.utm_medium,
    utm_campaign: utmParams.utm_campaign,
    utm_term: utmParams.utm_term,
    utm_content: utmParams.utm_content,
    utm_id: utmParams.utm_id
  };
};

/**
 * Clears stored UTM parameters
 */
const clearUTMParams = () => {
  localStorage.removeItem(UTM_STORAGE_KEY);
  console.log('UTM parameters cleared');
};

// First touch: written once per browser, never overwritten. Registered as
// analytics super properties (see analyticsService.init).
const FIRST_TOUCH_KEY = 'fleek_first_touch';

const captureFirstTouch = () => {
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const urlParams = new URLSearchParams(window.location.search);
    const firstTouch = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_content: urlParams.get('utm_content'),
      utm_term: urlParams.get('utm_term'),
      referrer: document.referrer || null,
      landing_path: window.location.pathname,
    };
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  } catch (e) {
    /* storage blocked: attribution is best effort */
  }
};

const getFirstTouch = () => {
  try {
    return JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null');
  } catch (e) {
    return null;
  }
};

const UTMTracker = {
  captureUTMParams,
  captureFirstTouch,
  getFirstTouch,
  getUTMParams,
  addUTMNotesToPayload, // Keep same name for backward compatibility
  getUTMForAnalytics,
  clearUTMParams
};

export default UTMTracker; 